/**
 * 클래스 상세 — 학원 모드. 정보·학생·출석·과제·성적·공지 탭.
 * (과제·성적은 후속 — 우선 정보/학생/출석/공지 동작)
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import {
  IoArrowBack, IoAdd, IoTrashOutline, IoClose,
  IoChevronBack, IoChevronForward, IoPersonCircleOutline,
} from 'react-icons/io5';
import { ref as storageRef, uploadBytes, getDownloadURL } from 'firebase/storage';
import { storage } from '../../firebase/config';
import { useAcademy } from '../../context/AcademyContext';
import academyService from '../../services/academyService';
import BrandLoader from '../../components/common/BrandLoader';
import Badge from '../../components/common/Badge';

const OPT_TABS = [
  { tab: '과제', key: 'assignment' },
  { tab: '성적', key: 'grade' },
  { tab: '공지', key: 'notice' },
  { tab: '자료', key: 'material' },
  { tab: '수업일지', key: 'log' },
];
const DEFAULT_FEATURES = { assignment: true, grade: true, notice: true, material: true, log: true };
const DAYS = ['월', '화', '수', '목', '금', '토', '일'];

const todayStr = () => {
  const d = new Date();
  const p = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
};

const nowHM = () => {
  const d = new Date();
  const p = (n) => String(n).padStart(2, '0');
  return `${p(d.getHours())}:${p(d.getMinutes())}`;
};

const DAY_LABELS = ['일', '월', '화', '수', '목', '금', '토'];
const fmtDateK = (s) => {
  if (!s) return '';
  const [y, m, d] = s.split('-').map(Number);
  const dt = new Date(y, m - 1, d);
  return `${m}월 ${d}일 (${DAY_LABELS[dt.getDay()]})`;
};
const addDateStr = (s, delta) => {
  const [y, m, d] = s.split('-').map(Number);
  const dt = new Date(y, m - 1, d);
  dt.setDate(dt.getDate() + delta);
  const p = (n) => String(n).padStart(2, '0');
  return `${dt.getFullYear()}-${p(dt.getMonth() + 1)}-${p(dt.getDate())}`;
};
const STATUS_KO = { present: '출석', late: '지각', absent: '결석', excused: '사유결석' };

const BizClassDetailPage = () => {
  const { cid } = useParams();
  const navigate = useNavigate();
  const { operatedAcademy } = useAcademy();
  const aid = operatedAcademy?.id;

  const [cls, setCls] = useState(null);
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('출석');

  // 학생 배정
  const [assigned, setAssigned] = useState([]);
  const [savingStu, setSavingStu] = useState(false);
  const [stuModalOpen, setStuModalOpen] = useState(false);
  const [nameModalOpen, setNameModalOpen] = useState(false);
  const [nameInput, setNameInput] = useState('');
  const [schedModalOpen, setSchedModalOpen] = useState(false);
  const [editSched, setEditSched] = useState([]);
  const [editDay, setEditDay] = useState('월');
  const [editTime, setEditTime] = useState('');
  const [savingMeta, setSavingMeta] = useState(false);

  // 출석
  const [attDate, setAttDate] = useState(todayStr());
  const [records, setRecords] = useState({});
  const [attLog, setAttLog] = useState({});
  const [sheetSid, setSheetSid] = useState(null); // 학생 카드 탭 시 액션 시트 띄울 학생 id

  // 공지
  const [notices, setNotices] = useState([]);
  const [nTitle, setNTitle] = useState('');
  const [nContent, setNContent] = useState('');

  // 과제
  const [assignments, setAssignments] = useState([]);
  const [aTitle, setATitle] = useState('');
  const [aDesc, setADesc] = useState('');
  const [aDue, setADue] = useState('');
  const [asgModalOpen, setAsgModalOpen] = useState(false);
  const [noticeModalOpen, setNoticeModalOpen] = useState(false);
  const [asgDetail, setAsgDetail] = useState(null);

  // 자료실
  const [materials, setMaterials] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [academy, setAcademy] = useState(null);

  // 수업일지
  const [logs, setLogs] = useState([]);
  const [logModalOpen, setLogModalOpen] = useState(false);
  const [logDate, setLogDate] = useState(todayStr());
  const [logContent, setLogContent] = useState('');
  const [logSaving, setLogSaving] = useState(false);

  // 성적
  const [grades, setGrades] = useState([]);
  const [gradeModalOpen, setGradeModalOpen] = useState(false);
  const [editingGrade, setEditingGrade] = useState(null); // null = 신규, 객체 = 수정
  const [gradeTitle, setGradeTitle] = useState('');
  const [gradeMax, setGradeMax] = useState(100);
  const [scoreInput, setScoreInput] = useState({});
  const [gradeSaving, setGradeSaving] = useState(false);

  const load = useCallback(async () => {
    if (!aid || !cid) { setLoading(false); return; }
    const [c, stu, noti, mats, aca, asgs, grds, lgs] = await Promise.all([
      academyService.getClass(aid, cid),
      academyService.getStudents(aid),
      academyService.getNotices(aid, cid),
      academyService.getMaterials(aid, cid),
      academyService.getAcademy(aid),
      academyService.getAssignments(aid, cid),
      academyService.getGrades(aid, cid),
      academyService.getLogs(aid, cid),
    ]);
    setAssignments(asgs);
    setCls(c);
    setStudents(stu);
    setAssigned(c?.studentIds || []);
    setNotices(noti);
    setMaterials(mats);
    setAcademy(aca);
    setGrades(grds);
    setLogs(lgs);
    setLoading(false);
  }, [aid, cid]);

  useEffect(() => { load(); }, [load]);

  // 클래스 출석 전체 로드 (주간 요약용)
  useEffect(() => {
    if (!aid || !cid) return;
    academyService.getClassAttendanceLog(aid, cid).then((log) => {
      const map = {};
      log.forEach((d) => { map[d.date] = d.records || {}; });
      setAttLog(map);
    });
  }, [aid, cid]);

  // 선택한 날짜 → 출석 records
  useEffect(() => {
    const raw = attLog[attDate] || {};
    const norm = {};
    Object.entries(raw).forEach(([sid, v]) => {
      norm[sid] = typeof v === 'string' ? { status: v } : v;
    });
    setRecords(norm);
  }, [attLog, attDate]);

  const toggleStudent = (sid) => {
    setAssigned((a) => (a.includes(sid) ? a.filter((x) => x !== sid) : [...a, sid]));
  };

  const saveStudents = async () => {
    setSavingStu(true);
    try {
      await academyService.updateClass(aid, cid, { studentIds: assigned, students: assigned.length });
      setCls((c) => ({ ...c, studentIds: assigned, students: assigned.length }));
    } finally { setSavingStu(false); }
  };

  /**
   * 단일 학생 출석 변경 — 액션 시트에서 호출. 즉시 저장.
   * action: 'present' | 'late' | 'absent' | 'excused' | 'out' | 'clear'
   */
  const updateStudentAtt = async (sid, action) => {
    const r = records[sid] || {};
    const now = nowHM();
    let nextRec = null;
    if (action === 'present') {
      nextRec = { status: 'present', inAt: r.inAt || now };
      if (r.outAt) nextRec.outAt = r.outAt;
    } else if (action === 'late') {
      nextRec = { status: 'late', inAt: r.inAt || now };
      if (r.outAt) nextRec.outAt = r.outAt;
    } else if (action === 'absent') {
      nextRec = { status: 'absent' };
    } else if (action === 'excused') {
      nextRec = { status: 'excused' };
    } else if (action === 'out') {
      if (r.status !== 'present' && r.status !== 'late') return;
      nextRec = { ...r, outAt: r.outAt || now };
    } else if (action === 'clear') {
      nextRec = null; // 미체크
    }
    const all = { ...records };
    if (nextRec === null) delete all[sid];
    else all[sid] = nextRec;
    setRecords(all);
    try {
      await academyService.saveClassAttendance(aid, cid, attDate, all);
      setAttLog((p) => ({ ...p, [attDate]: all }));
    } catch (e) {
      console.error('출석 저장 실패:', e);
      alert('출석 저장에 실패했어요.');
    }
  };

  const addNotice = async () => {
    const t = nTitle.trim();
    if (!t) return;
    await academyService.addNotice(aid, cid, { title: t, content: nContent.trim() });
    setNTitle('');
    setNContent('');
    setNotices(await academyService.getNotices(aid, cid));
  };

  const removeNotice = async (nid) => {
    if (!window.confirm('이 공지를 삭제할까요?')) return;
    await academyService.deleteNotice(aid, cid, nid);
    setNotices(await academyService.getNotices(aid, cid));
  };

  /* ── 성적 ─────────────────────────────────────── */
  const openNewGradeModal = () => {
    setEditingGrade(null);
    setGradeTitle('');
    setGradeMax(100);
    setScoreInput({});
    setGradeModalOpen(true);
  };

  const openEditGradeModal = (g) => {
    setEditingGrade(g);
    setGradeTitle(g.title || '');
    setGradeMax(g.max || 100);
    const input = {};
    Object.entries(g.scores || {}).forEach(([sid, v]) => { input[sid] = String(v); });
    setScoreInput(input);
    setGradeModalOpen(true);
  };

  const closeGradeModal = () => {
    setGradeModalOpen(false);
    setEditingGrade(null);
  };

  const saveGrade = async () => {
    const title = gradeTitle.trim();
    if (!title) return;
    setGradeSaving(true);
    try {
      const scores = {};
      Object.entries(scoreInput).forEach(([sid, raw]) => {
        const v = String(raw).trim();
        if (v === '') return;
        const n = Number(v);
        if (!Number.isNaN(n)) scores[sid] = n;
      });
      const max = Number(gradeMax) || 100;
      if (editingGrade) {
        await academyService.updateGrade(aid, cid, editingGrade.id, { title, max, scores });
      } else {
        await academyService.addGrade(aid, cid, { title, max, scores });
      }
      setGrades(await academyService.getGrades(aid, cid));
      closeGradeModal();
    } finally {
      setGradeSaving(false);
    }
  };

  const removeGrade = async () => {
    if (!editingGrade) return;
    if (!window.confirm(`'${editingGrade.title}' 성적을 삭제할까요?`)) return;
    await academyService.deleteGrade(aid, cid, editingGrade.id);
    setGrades(await academyService.getGrades(aid, cid));
    closeGradeModal();
  };

  const removeClass = async () => {
    if (!window.confirm(`'${cls.name}' 수업을 삭제할까요?\n출석·공지 등 수업 데이터가 함께 사라지고 되돌릴 수 없어요.`)) return;
    await academyService.deleteClass(aid, cid);
    navigate('/app/biz/classes');
  };

  const openNameEdit = () => { setNameInput(cls?.name || ''); setNameModalOpen(true); };
  const saveName = async () => {
    const v = nameInput.trim();
    if (!v) return;
    setSavingMeta(true);
    try {
      await academyService.updateClass(aid, cid, { name: v });
      setCls((c) => ({ ...c, name: v }));
      setNameModalOpen(false);
    } finally { setSavingMeta(false); }
  };

  const openSchedEdit = () => {
    setEditSched(cls?.schedule || []);
    setEditDay('월');
    setEditTime('');
    setSchedModalOpen(true);
  };
  const addEditSlot = () => {
    const t = editTime.trim();
    if (!t) return;
    setEditSched((s) => [...s, { day: editDay, time: t }]);
    setEditTime('');
  };
  const removeEditSlot = (i) => setEditSched((s) => s.filter((_, idx) => idx !== i));
  const saveSchedule = async () => {
    setSavingMeta(true);
    try {
      await academyService.updateClass(aid, cid, { schedule: editSched });
      setCls((c) => ({ ...c, schedule: editSched }));
      setSchedModalOpen(false);
    } finally { setSavingMeta(false); }
  };

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const path = `class-materials/${aid}-${cid}-${Date.now()}-${file.name}`;
      const snap = await uploadBytes(storageRef(storage, path), file);
      const url = await getDownloadURL(snap.ref);
      await academyService.addMaterial(aid, cid, { name: file.name, url });
      setMaterials(await academyService.getMaterials(aid, cid));
    } catch (err) {
      console.error('자료 업로드 실패:', err);
      alert('업로드에 실패했어요.');
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  const addAssignment = async () => {
    const t = aTitle.trim();
    if (!t) return;
    await academyService.addAssignment(aid, cid, {
      title: t,
      description: aDesc.trim(),
      dueDate: aDue,
    });
    setATitle(''); setADesc(''); setADue('');
    setAssignments(await academyService.getAssignments(aid, cid));
  };

  const openAsgDetail = (asg) => setAsgDetail(asg);

  const removeAssignment = async (asgId, title) => {
    // eslint-disable-next-line no-alert
    if (!window.confirm(`'${title}' 과제를 삭제할까요?`)) return;
    await academyService.deleteAssignment(aid, cid, asgId);
    setAssignments(await academyService.getAssignments(aid, cid));
  };

  const removeMaterial = async (mid) => {
    if (!window.confirm('이 자료를 삭제할까요?')) return;
    await academyService.deleteMaterial(aid, cid, mid);
    setMaterials(await academyService.getMaterials(aid, cid));
  };

  if (loading) return <BrandLoader fullscreen />;
  if (!cls) return <Center>수업을 찾을 수 없어요.</Center>;

  const classStudents = students.filter((s) => (cls.studentIds || []).includes(s.id));
  const changed = JSON.stringify([...assigned].sort()) !== JSON.stringify([...(cls.studentIds || [])].sort());
  const features = academy?.features || DEFAULT_FEATURES;
  // 정보·학생·출석은 한 화면에 섹션으로, 과제·성적·공지·자료만 탭으로
  // 출석은 기본 탭 — 기능 토글과 무관하게 항상 노출
  const tabs = ['출석', ...OPT_TABS.filter((o) => features[o.key]).map((o) => o.tab)];

  return (
    <Wrap>
      <TopBar>
        <BackBtn onClick={() => navigate('/app/biz/classes')}><IoArrowBack size={22} /></BackBtn>
        <TopSpacer />
        <HeaderDeleteBtn type="button" onClick={removeClass}>수업 삭제</HeaderDeleteBtn>
      </TopBar>

      <Body>
        <ClassHero>
          <ClassName>{cls.name}</ClassName>
        </ClassHero>

        <SecHead>정보</SecHead>
        <Card>
          <InfoRow>
            <InfoK>수업 이름</InfoK>
            <InfoV>
              {cls.name}
              <ManageBtn type="button" onClick={openNameEdit}>변경</ManageBtn>
            </InfoV>
          </InfoRow>
          <InfoRow>
            <InfoK>수업 시간</InfoK>
            <InfoV>
              {cls.schedule?.length > 0
                ? cls.schedule.map((s, i) => <Badge key={i} $size="sm">{s.day} {s.time}</Badge>)
                : '-'}
              <ManageBtn type="button" onClick={openSchedEdit}>변경</ManageBtn>
            </InfoV>
          </InfoRow>
          <InfoRow>
            <InfoK>학생 수</InfoK>
            <InfoV>
              {cls.students || 0}명
              <ManageBtn type="button" onClick={() => setStuModalOpen(true)}>관리</ManageBtn>
            </InfoV>
          </InfoRow>
        </Card>

        <Tabs>
          {tabs.map((t) => (
            <TabBtn key={t} $on={tab === t} onClick={() => setTab(t)}>{t}</TabBtn>
          ))}
        </Tabs>

        {tab === '출석' && (() => {
          const isToday = attDate === todayStr();
          let pCnt = 0; let lCnt = 0; let aCnt = 0; let eCnt = 0;
          classStudents.forEach((s) => {
            const st = records[s.id]?.status;
            if (st === 'present') pCnt += 1;
            else if (st === 'late') lCnt += 1;
            else if (st === 'absent') aCnt += 1;
            else if (st === 'excused') eCnt += 1;
          });
          const uncheckedN = classStudents.length - pCnt - lCnt - aCnt - eCnt;
          return (
            <AttPanel>
              <DateNav>
                <NavArrow type="button" onClick={() => setAttDate(addDateStr(attDate, -1))}>
                  <IoChevronBack size={18} />
                </NavArrow>
                <DateMain>
                  <DateText>{fmtDateK(attDate)}</DateText>
                  {isToday && <TodayChip>오늘</TodayChip>}
                </DateMain>
                <NavArrow
                  type="button"
                  onClick={() => setAttDate(addDateStr(attDate, 1))}
                  disabled={isToday}
                >
                  <IoChevronForward size={18} />
                </NavArrow>
              </DateNav>
              {classStudents.length > 0 && (
                <SummaryRow>
                  <Sum $kind="p">출석 <b>{pCnt}</b></Sum>
                  <Sum $kind="l">지각 <b>{lCnt}</b></Sum>
                  <Sum $kind="a">결석 <b>{aCnt}</b></Sum>
                  <Sum $kind="e">사유 <b>{eCnt}</b></Sum>
                  <Sum>미체크 <b>{uncheckedN}</b></Sum>
                </SummaryRow>
              )}

              {classStudents.length === 0 ? (
                <Empty><EmptyText>먼저 학생을 배정하세요.</EmptyText></Empty>
              ) : (
                <StuGrid>
                  {classStudents.map((s) => {
                    const r = records[s.id] || {};
                    const st = r.status;
                    return (
                      <StuCard key={s.id} type="button" $status={st} onClick={() => setSheetSid(s.id)}>
                        <CardTop>
                          <Avatar $photo={s.photoUrl} />
                          <StuMain>
                            <StuTopRow>
                              <StuName>{s.name}</StuName>
                              {s.age != null && s.age !== '' && <StuAge>{s.age}세</StuAge>}
                            </StuTopRow>
                            {st && <StatusBadge $kind={st}>{STATUS_KO[st]}</StatusBadge>}
                          </StuMain>
                        </CardTop>
                        {(r.inAt || r.outAt) && (
                          <StuTime>
                            {r.inAt && <span>등원 {r.inAt}</span>}
                            {r.outAt && <span> · 하원 {r.outAt}</span>}
                          </StuTime>
                        )}
                      </StuCard>
                    );
                  })}
                </StuGrid>
              )}
            </AttPanel>
          );
        })()}

        {tab === '공지' && (
          <>
            <TabHead>
              <AddTabBtn type="button" onClick={() => setNoticeModalOpen(true)}>
                <IoAdd size={17} /> 공지 등록
              </AddTabBtn>
            </TabHead>
            {notices.length === 0 ? (
              <Empty><EmptyText>등록된 공지가 없어요.</EmptyText></Empty>
            ) : (
              notices.map((n) => (
                <NoticeCard key={n.id}>
                  <NoticeHead>
                    <NoticeTitle>{n.title}</NoticeTitle>
                    <DelBtn onClick={() => removeNotice(n.id)}><IoTrashOutline size={16} /></DelBtn>
                  </NoticeHead>
                  {n.content && <NoticeBody>{n.content}</NoticeBody>}
                </NoticeCard>
              ))
            )}
          </>
        )}

        {tab === '자료' && (
          <>
            <TabHead>
              <AddTabBtn as="label" $disabled={uploading}>
                <IoAdd size={17} />
                {uploading ? '업로드 중...' : '자료 올리기'}
                <HiddenFile type="file" onChange={handleUpload} disabled={uploading} />
              </AddTabBtn>
            </TabHead>
            {materials.length === 0 ? (
              <Empty><EmptyText>등록된 자료가 없어요.</EmptyText></Empty>
            ) : (
              materials.map((m) => (
                <MatRow key={m.id}>
                  <MatLink href={m.url} target="_blank" rel="noreferrer">{m.name}</MatLink>
                  <DelBtn type="button" onClick={() => removeMaterial(m.id)}><IoTrashOutline size={16} /></DelBtn>
                </MatRow>
              ))
            )}
          </>
        )}

        {tab === '수업일지' && (
          <>
            <TabHead>
              <AddTabBtn
                type="button"
                onClick={() => {
                  setLogDate(todayStr()); setLogContent(''); setLogModalOpen(true);
                }}
              >
                <IoAdd size={17} /> 수업일지 작성
              </AddTabBtn>
            </TabHead>
            {logs.length === 0 ? (
              <Empty><EmptyText>등록된 수업일지가 없어요.</EmptyText></Empty>
            ) : (
              logs.map((l) => (
                <LogRow key={l.id}>
                  <LogTop>
                    <LogDate>{l.date}</LogDate>
                    <DelBtn
                      onClick={async () => {
                        if (!window.confirm('수업일지를 삭제할까요?')) return;
                        await academyService.deleteLog(aid, cid, l.id);
                        setLogs(await academyService.getLogs(aid, cid));
                      }}
                    >
                      <IoTrashOutline size={16} />
                    </DelBtn>
                  </LogTop>
                  {l.content && <LogContent>{l.content}</LogContent>}
                </LogRow>
              ))
            )}
          </>
        )}

        {tab === '과제' && (
          <>
            <TabHead>
              <AddTabBtn type="button" onClick={() => setAsgModalOpen(true)}>
                <IoAdd size={17} /> 과제 추가
              </AddTabBtn>
            </TabHead>

            {assignments.length === 0 ? (
              <Empty><EmptyText>등록된 과제가 없어요.</EmptyText></Empty>
            ) : (
              assignments.map((a) => {
                const subs = a.submissions || {};
                const submittedN = Object.values(subs).filter((s) => s?.submitted).length;
                const totalN = (cls.studentIds || []).length;
                return (
                  <AsgCard key={a.id} onClick={() => openAsgDetail(a)}>
                    <AsgTop>
                      <AsgTitle>{a.title}</AsgTitle>
                      <AsgDel
                        type="button"
                        onClick={(e) => { e.stopPropagation(); removeAssignment(a.id, a.title); }}
                      >
                        <IoTrashOutline size={16} />
                      </AsgDel>
                    </AsgTop>
                    {a.description && <AsgDesc>{a.description}</AsgDesc>}
                    <AsgMeta>
                      {a.dueDate && <span>마감 {a.dueDate}</span>}
                      <AsgStat $kind="ok">제출 {submittedN}/{totalN}</AsgStat>
                    </AsgMeta>
                  </AsgCard>
                );
              })
            )}
          </>
        )}

        {tab === '성적' && (
          <>
            <TabHead>
              <AddTabBtn type="button" onClick={openNewGradeModal}>
                <IoAdd size={17} /> 성적 등록
              </AddTabBtn>
            </TabHead>
            {grades.length === 0 ? (
              <Empty><EmptyText>등록된 성적이 없어요.</EmptyText></Empty>
            ) : (
              grades.map((g) => {
                const sids = Object.keys(g.scores || {});
                const vals = sids.map((sid) => Number(g.scores[sid] || 0));
                const total = vals.reduce((s, v) => s + v, 0);
                const avg = sids.length > 0 ? Math.round((total / sids.length) * 10) / 10 : 0;
                return (
                  <GradeCard key={g.id} type="button" onClick={() => openEditGradeModal(g)}>
                    <GradeCardTop>
                      <GradeCardTitle>{g.title}</GradeCardTitle>
                      <GradeCardCount>{sids.length}명</GradeCardCount>
                    </GradeCardTop>
                    <GradeCardMeta>평균 {avg} / {g.max || 100}점</GradeCardMeta>
                  </GradeCard>
                );
              })
            )}
          </>
        )}
      </Body>

      {stuModalOpen && (
        <Overlay onClick={() => setStuModalOpen(false)}>
          <Modal onClick={(e) => e.stopPropagation()}>
            <ModalHead>
              <ModalTitle>학생 배정</ModalTitle>
              <ModalClose type="button" onClick={() => setStuModalOpen(false)}>
                <IoClose size={22} />
              </ModalClose>
            </ModalHead>
            <ModalBody>
              {students.length === 0 ? (
                <Empty>
                  <EmptyText>학원에 등록된 학생이 없어요.</EmptyText>
                  <EmptyBtn onClick={() => navigate('/app/biz/students')}>학생 등록하러 가기</EmptyBtn>
                </Empty>
              ) : (
                <>
                  <SectionHint>이 수업에 배정할 학생을 선택하세요.</SectionHint>
                  <PickGrid>
                    {students.map((s) => {
                      const on = assigned.includes(s.id);
                      return (
                        <PickCard key={s.id} type="button" $on={on} onClick={() => toggleStudent(s.id)}>
                          <CardTop>
                            <Avatar $photo={s.photoUrl} />
                            <StuMain>
                              <StuTopRow>
                                <StuName>{s.name}</StuName>
                                {s.age != null && s.age !== '' && <StuAge>{s.age}세</StuAge>}
                              </StuTopRow>
                              <PickBadge $on={on}>{on ? '✓ 배정됨' : '미배정'}</PickBadge>
                            </StuMain>
                          </CardTop>
                        </PickCard>
                      );
                    })}
                  </PickGrid>
                </>
              )}
            </ModalBody>
            {students.length > 0 && (
              <SaveBtn
                type="button"
                disabled={savingStu || !changed}
                onClick={async () => { await saveStudents(); setStuModalOpen(false); }}
              >
                {savingStu ? '저장 중...' : `배정 저장 (${assigned.length}명)`}
              </SaveBtn>
            )}
          </Modal>
        </Overlay>
      )}

      {sheetSid && (() => {
        const s = classStudents.find((x) => x.id === sheetSid);
        if (!s) return null;
        const r = records[sheetSid] || {};
        const canOut = (r.status === 'present' || r.status === 'late') && !r.outAt;
        const closeAfter = (action) => async () => {
          await updateStudentAtt(sheetSid, action);
          setSheetSid(null);
        };
        return (
          <SheetOverlay onClick={() => setSheetSid(null)}>
            <ActionSheet onClick={(e) => e.stopPropagation()}>
              <SheetHead>
                <Avatar $photo={s.photoUrl} $sm />
                <SheetHText>
                  <SheetHName>{s.name}{s.age != null && s.age !== '' ? ` · ${s.age}세` : ''}</SheetHName>
                  <SheetHDate>{fmtDateK(attDate)} 출석 처리</SheetHDate>
                </SheetHText>
              </SheetHead>
              <ActGrid>
                <ActBtn $kind="p" $on={r.status === 'present'} onClick={closeAfter('present')}>출석</ActBtn>
                <ActBtn $kind="l" $on={r.status === 'late'} onClick={closeAfter('late')}>지각</ActBtn>
                <ActBtn $kind="a" $on={r.status === 'absent'} onClick={closeAfter('absent')}>결석</ActBtn>
                <ActBtn $kind="e" $on={r.status === 'excused'} onClick={closeAfter('excused')}>사유결석</ActBtn>
              </ActGrid>
              {canOut && (
                <ActWide $kind="o" onClick={closeAfter('out')}>지금 하원 시각 찍기</ActWide>
              )}
              {r.status && (
                <ActWide $kind="x" onClick={closeAfter('clear')}>미체크로 되돌리기</ActWide>
              )}
              <ActCancel type="button" onClick={() => setSheetSid(null)}>닫기</ActCancel>
            </ActionSheet>
          </SheetOverlay>
        );
      })()}

      {nameModalOpen && (
        <Overlay onClick={() => setNameModalOpen(false)}>
          <Modal onClick={(e) => e.stopPropagation()}>
            <ModalHead>
              <ModalTitle>수업 이름 변경</ModalTitle>
              <ModalClose type="button" onClick={() => setNameModalOpen(false)}>
                <IoClose size={22} />
              </ModalClose>
            </ModalHead>
            <ModalBody>
              <FInput
                autoFocus
                value={nameInput}
                onChange={(e) => setNameInput(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); saveName(); } }}
              />
            </ModalBody>
            <SaveBtn type="button" disabled={savingMeta || !nameInput.trim()} onClick={saveName}>
              {savingMeta ? '저장 중...' : '저장'}
            </SaveBtn>
          </Modal>
        </Overlay>
      )}

      {schedModalOpen && (
        <Overlay onClick={() => setSchedModalOpen(false)}>
          <Modal onClick={(e) => e.stopPropagation()}>
            <ModalHead>
              <ModalTitle>수업 시간 변경</ModalTitle>
              <ModalClose type="button" onClick={() => setSchedModalOpen(false)}>
                <IoClose size={22} />
              </ModalClose>
            </ModalHead>
            <ModalBody>
              <DayRow>
                {DAYS.map((d) => (
                  <DayBtn key={d} type="button" $on={editDay === d} onClick={() => setEditDay(d)}>{d}</DayBtn>
                ))}
              </DayRow>
              <SlotAdd>
                <FInput
                  value={editTime}
                  onChange={(e) => setEditTime(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addEditSlot(); } }}
                  style={{ flex: 1 }}
                />
                <AddSlotBtn type="button" onClick={addEditSlot}><IoAdd size={18} /></AddSlotBtn>
              </SlotAdd>
              <SlotHint>요일 선택 후 시간 입력 · 예: 13:00~14:00</SlotHint>
              {editSched.length > 0 && (
                <SlotList>
                  {editSched.map((s, i) => (
                    // eslint-disable-next-line react/no-array-index-key
                    <SlotChip key={i} type="button" onClick={() => removeEditSlot(i)}>
                      {s.day} {s.time} <IoClose size={12} />
                    </SlotChip>
                  ))}
                </SlotList>
              )}
            </ModalBody>
            <SaveBtn type="button" disabled={savingMeta} onClick={saveSchedule}>
              {savingMeta ? '저장 중...' : '저장'}
            </SaveBtn>
          </Modal>
        </Overlay>
      )}

      {noticeModalOpen && (
        <Overlay onClick={() => setNoticeModalOpen(false)}>
          <Modal onClick={(e) => e.stopPropagation()}>
            <ModalHead>
              <ModalTitle>공지 등록</ModalTitle>
              <ModalClose type="button" onClick={() => setNoticeModalOpen(false)}>
                <IoClose size={22} />
              </ModalClose>
            </ModalHead>
            <FLabel>공지 제목</FLabel>
            <FInput autoFocus value={nTitle} onChange={(e) => setNTitle(e.target.value)} />
            <FLabel>내용</FLabel>
            <FArea rows={4} value={nContent} onChange={(e) => setNContent(e.target.value)} />
            <SaveBtn
              type="button"
              disabled={!nTitle.trim()}
              onClick={async () => { await addNotice(); setNoticeModalOpen(false); }}
            >
              <IoAdd size={17} /> 공지 등록
            </SaveBtn>
          </Modal>
        </Overlay>
      )}

      {asgDetail && (
        <Overlay onClick={() => setAsgDetail(null)}>
          <Modal onClick={(e) => e.stopPropagation()}>
            <ModalHead>
              <ModalTitle>{asgDetail.title} · 제출 현황</ModalTitle>
              <ModalClose type="button" onClick={() => setAsgDetail(null)}>
                <IoClose size={22} />
              </ModalClose>
            </ModalHead>
            <ModalBody>
              {(cls.studentIds || []).length === 0 ? (
                <Empty><EmptyText>배정된 학생이 없어요.</EmptyText></Empty>
              ) : (
                <SubList>
                  {(cls.studentIds || []).map((sid) => {
                    const stu = students.find((s) => s.id === sid);
                    const s = (asgDetail.submissions || {})[sid];
                    const submitted = !!s?.submitted;
                    return (
                      <SubRow key={sid}>
                        <SubName>{stu?.name || '학생'}</SubName>
                        <SubBadge $on={submitted}>{submitted ? '제출' : '미제출'}</SubBadge>
                      </SubRow>
                    );
                  })}
                </SubList>
              )}
            </ModalBody>
          </Modal>
        </Overlay>
      )}

      {logModalOpen && (
        <Overlay onClick={() => setLogModalOpen(false)}>
          <Modal onClick={(e) => e.stopPropagation()}>
            <ModalHead>
              <ModalTitle>수업일지 작성</ModalTitle>
              <ModalClose type="button" onClick={() => setLogModalOpen(false)}>
                <IoClose size={22} />
              </ModalClose>
            </ModalHead>
            <FLabel>날짜</FLabel>
            <FInput type="date" value={logDate} onChange={(e) => setLogDate(e.target.value)} />
            <FLabel>내용</FLabel>
            <FArea
              rows={6}
              value={logContent}
              onChange={(e) => setLogContent(e.target.value)}
            />
            <SaveBtn
              type="button"
              disabled={logSaving || !logContent.trim()}
              onClick={async () => {
                setLogSaving(true);
                try {
                  await academyService.addLog(aid, cid, { date: logDate, content: logContent.trim() });
                  setLogs(await academyService.getLogs(aid, cid));
                  setLogModalOpen(false);
                } finally { setLogSaving(false); }
              }}
            >
              {logSaving ? '저장 중...' : '수업일지 저장'}
            </SaveBtn>
          </Modal>
        </Overlay>
      )}

      {asgModalOpen && (
        <Overlay onClick={() => setAsgModalOpen(false)}>
          <Modal onClick={(e) => e.stopPropagation()}>
            <ModalHead>
              <ModalTitle>과제 추가</ModalTitle>
              <ModalClose type="button" onClick={() => setAsgModalOpen(false)}>
                <IoClose size={22} />
              </ModalClose>
            </ModalHead>
            <FLabel>과제 제목</FLabel>
            <FInput autoFocus value={aTitle} onChange={(e) => setATitle(e.target.value)} />
            <FLabel>설명</FLabel>
            <FArea rows={4} value={aDesc} onChange={(e) => setADesc(e.target.value)} />
            <FLabel>마감일</FLabel>
            <FInput type="date" value={aDue} onChange={(e) => setADue(e.target.value)} />
            <SaveBtn
              type="button"
              disabled={!aTitle.trim()}
              onClick={async () => { await addAssignment(); setAsgModalOpen(false); }}
            >
              <IoAdd size={18} /> 과제 추가
            </SaveBtn>
          </Modal>
        </Overlay>
      )}

      {gradeModalOpen && (
        <Overlay onClick={closeGradeModal}>
          <Modal onClick={(e) => e.stopPropagation()}>
            <ModalHead>
              <ModalTitle>{editingGrade ? '성적 수정' : '성적 등록'}</ModalTitle>
              <ModalClose type="button" onClick={closeGradeModal}>
                <IoClose size={22} />
              </ModalClose>
            </ModalHead>

            <FLabel>시험 제목</FLabel>
            <FInput
              autoFocus
              value={gradeTitle}
              onChange={(e) => setGradeTitle(e.target.value)}
            />

            <FLabel>만점</FLabel>
            <FInput
              type="number"
              inputMode="numeric"
              value={gradeMax}
              onChange={(e) => setGradeMax(e.target.value)}
            />

            <FLabel>학생별 점수</FLabel>
            {classStudents.length === 0 ? (
              <EmptyText>이 수업에 배정된 학생이 없어요.</EmptyText>
            ) : (
              <ScoreList>
                {classStudents.map((s) => (
                  <ScoreRow key={s.id}>
                    <Avatar $photo={s.photoUrl} $sm />
                    <ScoreNameBlock>
                      <ScoreName>{s.name}</ScoreName>
                      {s.age != null && s.age !== '' && <ScoreAge>{s.age}세</ScoreAge>}
                    </ScoreNameBlock>
                    <ScoreField>
                      <ScoreInput
                        type="number"
                        inputMode="numeric"
                        value={scoreInput[s.id] ?? ''}
                        onChange={(e) => setScoreInput({ ...scoreInput, [s.id]: e.target.value })}
                      />
                      <ScoreUnit>/ {gradeMax || 100}</ScoreUnit>
                    </ScoreField>
                  </ScoreRow>
                ))}
              </ScoreList>
            )}

            <GradeModalFooter>
              {editingGrade && (
                <GradeDeleteBtn type="button" onClick={removeGrade}>
                  <IoTrashOutline size={16} /> 삭제
                </GradeDeleteBtn>
              )}
              <SaveBtn
                type="button"
                disabled={!gradeTitle.trim() || gradeSaving}
                onClick={saveGrade}
              >
                {gradeSaving ? '저장 중...' : (editingGrade ? '수정 저장' : '성적 등록')}
              </SaveBtn>
            </GradeModalFooter>
          </Modal>
        </Overlay>
      )}
    </Wrap>
  );
};

export default BizClassDetailPage;

const Wrap = styled.div`
  min-height: 100vh;
  background: #fff;
`;

const TopBar = styled.div`
  height: 56px;
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 0 12px;
  border-bottom: 1px solid ${({ theme }) => theme.colors.divider};
`;

const BackBtn = styled.button`
  background: none;
  display: flex;
  color: ${({ theme }) => theme.colors.text.primary};
`;

const TopSpacer = styled.div`
  flex: 1;
`;

/* 본문 상단 — 수업명 헤드라인 (간판 카드) */
const ClassHero = styled.div`
  margin: 4px 0 18px;
  padding: 18px 16px;
  text-align: center;
  background: #fff;
  border: 2px solid ${({ theme }) => theme.colors.primary};
  border-radius: 14px;
  box-shadow: 0 1.5px 0 ${({ theme }) => theme.colors.primary};
`;

const ClassName = styled.h1`
  font-size: 22px;
  font-weight: 800;
  letter-spacing: -0.4px;
  color: ${({ theme }) => theme.colors.primary};
`;

const HeaderDeleteBtn = styled.button`
  flex-shrink: 0;
  background: #fff;
  color: #1A1A2E;
  font-size: 13px;
  font-weight: 700;
  padding: 6px 4px;
`;

const Tabs = styled.div`
  display: flex;
  gap: 6px;
  overflow-x: auto;
  margin: 26px 0 4px;
  padding-bottom: 8px;
  border-bottom: 1px solid ${({ theme }) => theme.colors.divider};
  -ms-overflow-style: none;
  scrollbar-width: none;
  &::-webkit-scrollbar { display: none; }
`;

const TabBtn = styled.button`
  flex: 1;
  min-width: 62px;
  padding: 9px 14px;
  border-radius: 999px;
  font-size: 14px;
  font-weight: 700;
  white-space: nowrap;
  transition: background 0.15s ease, color 0.15s ease;
  color: ${({ $on, theme }) => ($on ? theme.colors.primary : theme.colors.text.hint)};
  background: ${({ $on, theme }) => ($on ? theme.colors.primaryLight : 'transparent')};
`;

const Body = styled.div`
  max-width: 480px;
  margin: 0 auto;
  padding: 18px 16px 60px;
`;

const SecHead = styled.h3`
  font-size: 16px;
  font-weight: 800;
  color: ${({ theme }) => theme.colors.text.primary};
  margin: 22px 0 10px;
  &:first-child { margin-top: 2px; }
`;

const DayRow = styled.div`
  display: flex;
  gap: 6px;
  margin-bottom: 10px;
`;

const DayBtn = styled.button`
  flex: 1;
  padding: 10px 0;
  border-radius: 10px;
  font-size: 14px;
  font-weight: 700;
  background: ${({ $on, theme }) => ($on ? theme.colors.primaryLight : '#F4F5F7')};
  color: ${({ $on, theme }) => ($on ? theme.colors.primary : theme.colors.text.hint)};
`;

const SlotAdd = styled.div`
  display: flex;
  gap: 6px;
  margin-bottom: 6px;
`;

const AddSlotBtn = styled.button`
  flex-shrink: 0;
  width: 44px;
  border-radius: 11px;
  background: ${({ theme }) => theme.colors.primary};
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const SlotHint = styled.p`
  font-size: 12px;
  color: ${({ theme }) => theme.colors.text.hint};
  margin-bottom: 12px;
`;

const SlotList = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
`;

const SlotChip = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 7px 10px;
  border-radius: 8px;
  background: ${({ theme }) => theme.colors.primaryLight};
  color: ${({ theme }) => theme.colors.primary};
  font-size: 13px;
  font-weight: 700;
`;

const ManageBtn = styled.button`
  padding: 5px 12px;
  border-radius: 13px;
  font-size: 12px;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.primary};
  background: ${({ theme }) => theme.colors.primaryLight};
`;

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  z-index: 200;
  background: rgba(0, 0, 0, 0.4);
  display: flex;
  align-items: flex-end;
  justify-content: center;
`;

const Modal = styled.div`
  width: 100%;
  max-width: 480px;
  max-height: 80vh;
  display: flex;
  flex-direction: column;
  background: #fff;
  border-radius: 20px 20px 0 0;
  padding: 18px 18px 24px;
`;

const ModalHead = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 14px;
`;

const ModalTitle = styled.p`
  font-size: 17px;
  font-weight: 800;
  color: ${({ theme }) => theme.colors.text.primary};
`;

const ModalClose = styled.button`
  background: none;
  display: flex;
  color: ${({ theme }) => theme.colors.text.hint};
`;

const ModalBody = styled.div`
  flex: 1;
  overflow-y: auto;
  margin-bottom: 8px;
`;

const Card = styled.div`
  padding: 4px 16px;
  border-radius: 14px;
  background: #F4F5F7;
  margin-bottom: 12px;
`;

const InfoRow = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 16px;
  padding: 13px 0;
  border-bottom: 1px solid #E7E8EC;
  &:last-child { border-bottom: none; }
`;

const InfoK = styled.span`
  flex-shrink: 0;
  font-size: 13px;
  color: ${({ theme }) => theme.colors.text.hint};
`;

const InfoV = styled.span`
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-end;
  gap: 5px;
  font-size: 14px;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.text.primary};
`;

const Slot = styled.span`
  padding: 3px 8px;
  border-radius: 6px;
  font-size: 11.5px;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.primary};
  background: ${({ theme }) => theme.colors.primaryLight};
`;

const SectionHint = styled.p`
  font-size: 13px;
  color: ${({ theme }) => theme.colors.text.hint};
  margin-bottom: 10px;
`;


const TimeRow = styled.div`
  display: flex;
  gap: 6px;
`;

const TimeTag = styled.span`
  padding: 3px 9px;
  border-radius: 7px;
  font-size: 12px;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.text.primary};
  background: #F3F4F6;
`;

const SaveBtn = styled.button`
  width: 100%;
  margin-top: 14px;
  padding: 15px;
  border-radius: 13px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 5px;
  background: ${({ theme }) => theme.colors.primary};
  color: #fff;
  font-size: 15px;
  font-weight: 700;
  &:disabled { background: #D7D9E0; }
`;

const FLabel = styled.label`
  display: block;
  font-size: 13px;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.text.primary};
  margin: 14px 0 7px;
`;

const FInput = styled.input`
  width: 100%;
  padding: 12px 13px;
  border: 0;
  border-radius: 10px;
  background: #F4F5F7;
  font-size: 15px;
  &:focus { outline: none; }
`;

const FArea = styled.textarea`
  width: 100%;
  padding: 12px 13px;
  border: 0;
  border-radius: 10px;
  background: #fff;
  font-size: 15px;
  resize: vertical;
  &:focus { outline: 2px solid ${({ theme }) => theme.colors.primary}; }
`;

const NoticeCard = styled.div`
  padding: 14px 2px;
  border-bottom: 1px solid #F0F1F4;
  &:last-of-type { border-bottom: none; }
`;

const NoticeHead = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 10px;
`;

const NoticeTitle = styled.p`
  font-size: 15px;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.text.primary};
`;

const DelBtn = styled.button`
  background: none;
  flex-shrink: 0;
  color: ${({ theme }) => theme.colors.text.hint};
  display: flex;
`;

const NoticeBody = styled.p`
  font-size: 13.5px;
  line-height: 1.55;
  color: ${({ theme }) => theme.colors.text.secondary};
  margin-top: 6px;
  white-space: pre-line;
`;

const Empty = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  padding: 70px 0;
`;

const EmptyText = styled.p`
  font-size: 14px;
  color: ${({ theme }) => theme.colors.text.hint};
`;

const EmptyBtn = styled.button`
  padding: 11px 22px;
  border-radius: 20px;
  background: ${({ theme }) => theme.colors.primaryGradient};
  color: #fff;
  font-size: 13.5px;
  font-weight: 700;
`;

const UploadBtn = styled.div`
  width: 100%;
  padding: 14px;
  border-radius: 12px;
  background: ${({ theme }) => theme.colors.primaryLight};
  color: ${({ theme }) => theme.colors.primary};
  font-size: 14px;
  font-weight: 700;
  text-align: center;
  cursor: pointer;
  margin-bottom: 12px;
`;

const HiddenFile = styled.input`
  display: none;
`;

const MatRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  padding: 14px;
  margin-bottom: 8px;
  border-radius: 12px;
  background: #F4F5F7;
`;

const MatLink = styled.a`
  flex: 1;
  min-width: 0;
  font-size: 14px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text.primary};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;


const TabHead = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-top: 14px;
`;

const AttPanel = styled.div`
  margin-top: 16px;
`;

/* ── 날짜 네비 ─────────────────────────────────── */
const DateNav = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 10px;
  border-radius: 12px;
  background: #F4F5F7;
`;

const NavArrow = styled.button`
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 8px;
  background: #fff;
  color: ${({ theme }) => theme.colors.text.primary};
  &:disabled { opacity: 0.35; }
`;

const DateMain = styled.div`
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 7px;
`;

const DateText = styled.span`
  font-size: 15.5px;
  font-weight: 800;
  color: ${({ theme }) => theme.colors.text.primary};
`;

const TodayChip = styled.span`
  padding: 2px 8px;
  border-radius: 7px;
  font-size: 11px;
  font-weight: 800;
  color: #fff;
  background: ${({ theme }) => theme.colors.primary};
`;

/* ── 요약 칩 (한 줄 고정) ───────────────────────── */
const SummaryRow = styled.div`
  display: flex;
  gap: 5px;
  margin-top: 14px;
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
  scrollbar-width: none;
  &::-webkit-scrollbar { display: none; }
`;

const Sum = styled.span`
  flex: 1 1 0;
  min-width: 0;
  text-align: center;
  padding: 5px 6px;
  border-radius: 8px;
  font-size: 11.5px;
  font-weight: 700;
  white-space: nowrap;
  color: ${({ $kind }) => ({
    p: '#1E40AF', l: '#92400E', a: '#991B1B', e: '#4B5563',
  }[$kind] || '#6B7280')};
  background: ${({ $kind }) => ({
    p: '#DBEAFE', l: '#FEF3C7', a: '#FEE2E2', e: '#E5E7EB',
  }[$kind] || '#F4F5F7')};
  b { font-weight: 800; margin-left: 2px; }
`;

/* ── 학생 카드 그리드 ──────────────────────────── */
const StuGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 9px;
  margin-top: 14px;
`;

const StuCard = styled.button`
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 11px;
  border-radius: 13px;
  text-align: left;
  background: ${({ $status }) => ({
    present: '#EFF6FF',
    late: '#FEF9C3',
    absent: '#FEE2E2',
    excused: '#F3F4F6',
  }[$status] || '#fff')};
  border: 1px solid ${({ $status }) => ({
    present: '#C7DAFE',
    late: '#FDE68A',
    absent: '#FECACA',
    excused: '#E5E7EB',
  }[$status] || '#E5E7EB')};
  &:active { opacity: 0.7; }
`;

const CardTop = styled.div`
  display: flex;
  gap: 10px;
  min-width: 0;
`;

/* ── 학생 배정 모달 — 2열 카드 그리드 ───────────── */
const PickGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 8px;
`;

const PickCard = styled.button`
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding: 11px;
  border-radius: 13px;
  text-align: left;
  background: ${({ $on }) => ($on ? '#EFF6FF' : '#fff')};
  border: 2px solid ${({ $on, theme }) => ($on ? theme.colors.primary : '#E5E7EB')};
  &:active { opacity: 0.7; }
`;

const PickBadge = styled.span`
  align-self: flex-start;
  padding: 2px 8px;
  border-radius: 6px;
  font-size: 11px;
  font-weight: 800;
  color: ${({ $on, theme }) => ($on ? '#fff' : theme.colors.text.hint)};
  background: ${({ $on, theme }) => ($on ? theme.colors.primary : '#F4F5F7')};
`;

const AvatarBase = styled.div`
  width: ${({ $sm }) => ($sm ? '40px' : '46px')};
  height: ${({ $sm }) => ($sm ? '40px' : '46px')};
  flex-shrink: 0;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${({ $photo }) => ($photo ? `#fff url(${$photo}) center / cover no-repeat` : '#F0F1F4')};
  color: #9CA3AF;
`;

const Avatar = ({ $photo, $sm }) => (
  <AvatarBase $photo={$photo} $sm={$sm}>
    {!$photo && <IoPersonCircleOutline size={$sm ? 36 : 42} />}
  </AvatarBase>
);

const StuMain = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 3px;
`;

const StuTopRow = styled.div`
  display: flex;
  align-items: baseline;
  gap: 6px;
`;

const StuName = styled.span`
  font-size: 14.5px;
  font-weight: 800;
  color: ${({ theme }) => theme.colors.text.primary};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const StuAge = styled.span`
  font-size: 11.5px;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.text.hint};
  flex-shrink: 0;
`;

const StatusBadge = styled.span`
  align-self: flex-start;
  padding: 2px 7px;
  border-radius: 6px;
  font-size: 11px;
  font-weight: 800;
  color: ${({ $kind }) => ({
    present: '#1E40AF', late: '#92400E', absent: '#991B1B', excused: '#4B5563',
  }[$kind] || '#6B7280')};
  background: ${({ $kind }) => ({
    present: '#DBEAFE', late: '#FEF3C7', absent: '#FEE2E2', excused: '#E5E7EB',
  }[$kind] || '#F4F5F7')};
`;

const StuTime = styled.div`
  font-size: 11px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text.secondary};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

/* ── 액션 시트 ─────────────────────────────────── */
const SheetOverlay = styled.div`
  position: fixed;
  inset: 0;
  z-index: 200;
  background: rgba(0, 0, 0, 0.4);
  display: flex;
  align-items: flex-end;
  justify-content: center;
`;

const ActionSheet = styled.div`
  width: 100%;
  max-width: 480px;
  background: #fff;
  border-radius: 18px 18px 0 0;
  padding: 18px 16px 22px;
`;

const SheetHead = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 14px;
`;

const SheetHText = styled.div`
  flex: 1;
`;

const SheetHName = styled.p`
  font-size: 15.5px;
  font-weight: 800;
  color: ${({ theme }) => theme.colors.text.primary};
`;

const SheetHDate = styled.p`
  font-size: 12px;
  color: ${({ theme }) => theme.colors.text.hint};
  margin-top: 2px;
`;

const ActGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 8px;
`;

const ActBtn = styled.button`
  padding: 13px;
  border-radius: 11px;
  font-size: 14.5px;
  font-weight: 800;
  border: 1.5px solid ${({ $kind, $on }) => {
    if (!$on) return '#E5E7EB';
    return { p: '#4F46E5', l: '#D97706', a: '#DC2626', e: '#6B7280' }[$kind] || '#E5E7EB';
  }};
  background: ${({ $kind, $on }) => {
    if (!$on) return '#fff';
    return { p: '#DBEAFE', l: '#FEF3C7', a: '#FEE2E2', e: '#E5E7EB' }[$kind] || '#fff';
  }};
  color: ${({ $kind }) => ({
    p: '#1E40AF', l: '#92400E', a: '#991B1B', e: '#4B5563',
  }[$kind] || '#374151')};
`;

const ActWide = styled.button`
  width: 100%;
  margin-top: 8px;
  padding: 13px;
  border-radius: 11px;
  font-size: 14px;
  font-weight: 800;
  border: 1.5px solid ${({ $kind }) => ($kind === 'o' ? '#4F46E5' : '#E5E7EB')};
  background: ${({ $kind }) => ($kind === 'o' ? '#EEF2FF' : '#fff')};
  color: ${({ $kind, theme }) => ($kind === 'o' ? theme.colors.primary : theme.colors.text.secondary)};
`;

const ActCancel = styled.button`
  width: 100%;
  margin-top: 12px;
  padding: 12px;
  border-radius: 11px;
  background: #F4F5F7;
  font-size: 14px;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.text.secondary};
`;

const AddTabBtn = styled.button`
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 8px 14px;
  border-radius: 11px;
  background: #fff;
  border: 1px solid ${({ theme }) => theme.colors.border};
  color: ${({ theme }) => theme.colors.text.primary};
  font-size: 13px;
  font-weight: 700;
`;

const AsgCard = styled.div`
  padding: 14px 2px;
  border-bottom: 1px solid #F0F1F4;
  cursor: pointer;
  &:last-of-type { border-bottom: none; }
  &:active { background: #FAFBFC; }
`;

const LogRow = styled.div`
  padding: 13px 2px;
  border-bottom: 1px solid #F0F1F4;
  &:last-of-type { border-bottom: none; }
`;

const LogTop = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
`;

const LogDate = styled.span`
  font-size: 14px;
  font-weight: 800;
  color: ${({ theme }) => theme.colors.primary};
`;

const LogContent = styled.p`
  margin-top: 6px;
  font-size: 13.5px;
  line-height: 1.55;
  white-space: pre-line;
  color: ${({ theme }) => theme.colors.text.secondary};
`;

const SubList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const SubRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 12px;
  border-radius: 10px;
  background: #FAFBFC;
`;

const SubName = styled.span`
  font-size: 14.5px;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.text.primary};
`;

const SubBadge = styled.span`
  padding: 4px 10px;
  border-radius: 11px;
  font-size: 12px;
  font-weight: 700;
  color: ${({ $on }) => ($on ? '#1E40AF' : '#92400E')};
  background: ${({ $on }) => ($on ? '#DBEAFE' : '#FEF3C7')};
`;

// 사용 안 함 (이전 채점 모달 잔재) — 빌드 경고 방지용
// eslint-disable-next-line no-unused-vars
const GradeList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 7px;
`;

const GradeRow = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding: 10px 11px;
  border-radius: 11px;
  background: #FAFBFC;
`;

const GradeTopLine = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

const GradeName = styled.div`
  flex: 1;
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 14.5px;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.text.primary};
`;

const GradeBadge = styled.span`
  padding: 2px 7px;
  border-radius: 7px;
  font-size: 10.5px;
  font-weight: 700;
  color: #1E40AF;
  background: #DBEAFE;
`;

const GradeScore = styled.input`
  flex-shrink: 0;
  width: 88px;
  padding: 9px 10px;
  border: 0;
  border-radius: 9px;
  background: #fff;
  font-size: 14.5px;
  font-weight: 700;
  text-align: center;
  color: ${({ theme }) => theme.colors.text.primary};
  &:focus { outline: none; }
`;

const GradeFeedback = styled.input`
  width: 100%;
  padding: 9px 11px;
  border: 0;
  border-radius: 9px;
  background: #fff;
  font-size: 13px;
  color: ${({ theme }) => theme.colors.text.primary};
  &:focus { outline: none; }
`;

const AsgTop = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
`;

const AsgTitle = styled.p`
  flex: 1;
  font-size: 15px;
  font-weight: 800;
  color: ${({ theme }) => theme.colors.text.primary};
`;

const AsgDel = styled.button`
  background: none;
  color: ${({ theme }) => theme.colors.text.hint};
  padding: 4px;
`;

const AsgDesc = styled.p`
  margin-top: 6px;
  font-size: 13px;
  line-height: 1.5;
  color: ${({ theme }) => theme.colors.text.secondary};
  white-space: pre-line;
`;

const AsgMeta = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 6px;
  margin-top: 10px;
  font-size: 12px;
  color: ${({ theme }) => theme.colors.text.hint};
`;

const AsgStat = styled.span`
  padding: 3px 9px;
  border-radius: 9px;
  font-size: 11.5px;
  font-weight: 700;
  color: ${({ $kind }) => ($kind === 'warn' ? '#92400E' : '#1E40AF')};
  background: ${({ $kind }) => ($kind === 'warn' ? '#FEF3C7' : '#DBEAFE')};
`;

const Center = styled.p`
  text-align: center;
  padding: 80px 0;
  color: ${({ theme }) => theme.colors.text.hint};
`;

/* ── 성적 ─────────────────────────────────────── */
const GradeCard = styled.button`
  display: flex;
  flex-direction: column;
  width: 100%;
  gap: 6px;
  padding: 14px 16px;
  border-radius: 12px;
  background: #F9FAFB;
  text-align: left;
  margin-top: 10px;
  &:hover { background: #F3F4F6; }
`;

const GradeCardTop = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
`;

const GradeCardTitle = styled.span`
  font-size: 14.5px;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.text.primary};
`;

const GradeCardCount = styled.span`
  flex-shrink: 0;
  padding: 3px 9px;
  border-radius: 9px;
  font-size: 11.5px;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.primary};
  background: ${({ theme }) => theme.colors.primaryLight};
`;

const GradeCardMeta = styled.span`
  font-size: 12.5px;
  color: ${({ theme }) => theme.colors.text.secondary};
`;

const ScoreList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-top: 6px;
  max-height: 320px;
  overflow-y: auto;
`;

const ScoreRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 12px;
  border-radius: 11px;
  background: #F9FAFB;
`;

const ScoreNameBlock = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  align-items: baseline;
  gap: 6px;
`;

const ScoreName = styled.span`
  font-size: 14px;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.text.primary};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const ScoreAge = styled.span`
  flex-shrink: 0;
  font-size: 11.5px;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.text.hint};
`;

const ScoreField = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
`;

const ScoreInput = styled.input`
  width: 72px;
  padding: 8px 10px;
  border: 0;
  border-radius: 9px;
  background: #fff;
  font-size: 14px;
  font-weight: 600;
  text-align: right;
  &:focus { outline: 2px solid ${({ theme }) => theme.colors.primary}; }
`;

const ScoreUnit = styled.span`
  font-size: 12px;
  color: ${({ theme }) => theme.colors.text.hint};
  min-width: 30px;
`;

const GradeModalFooter = styled.div`
  display: flex;
  gap: 8px;
  margin-top: 14px;
  & > button { margin-top: 0; }
  /* SaveBtn (width:100%)이 모든 공간 먹지 않게 강제로 flex 비율 부여 */
  & > button:last-child { flex: 1; width: auto; }
`;

const GradeDeleteBtn = styled.button`
  flex: 0 0 auto;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 5px;
  padding: 14px 18px;
  border-radius: 12px;
  background: #FEF2F2;
  border: 1px solid #FECACA;
  color: #DC2626;
  font-size: 14px;
  font-weight: 700;
  white-space: nowrap;
`;
